import PageLayout from "../../components/PageLayout";
import { useResponsive } from "../../lib/useResponsive";

export default function ExitReturnsPage() {
  const { isMobile } = useResponsive();

  return (
    <PageLayout
      eyebrow="Exit Terms & Returns"
      title={<>Know exactly <em>what you get back</em>.</>}
      subtitle=""
    >
      {/* Exit Cards */}
      <div style={{ ...S.exitCard, padding: isMobile ? "16px" : 24 }}>
        <div style={{ ...S.ybadge, ...S.darkGreenBadge }}>⏱️ Exit Before 2 Years</div>
        <h3 style={{ ...S.cardH3, fontSize: isMobile ? 16 : 18 }}>Fixed Interest Return</h3>
        <ul style={S.bulletList}>
          <li style={S.bulletItem}>Fixed return of <strong>1–4% annualized</strong>, benchmarked to the US Treasury Market, prorated by exact days held</li>
          <li style={S.bulletItem}>Rate referenced against current US Treasury yields — transparent and market-anchored</li>
          <li style={S.bulletItem}>No market exposure — sheltered if markets drop</li>
          <li style={S.bulletItem}>No market upside — stable and predictable</li>
          <li style={S.bulletItem}>Emergency withdrawals considered case by case</li>
        </ul>
      </div>

      <div style={{ ...S.exitCard, padding: isMobile ? "16px" : 24 }}>
        <div style={{ ...S.ybadge, ...S.lightGreenBadge }}>🚀 Exit After 2 Years</div>
        <h3 style={{ ...S.cardH3, fontSize: isMobile ? 16 : 18 }}>Full Market Participation</h3>
        <ul style={S.bulletList}>
          <li style={S.bulletItem}>Redemption at <strong>prevailing unit price</strong> at time of exit</li>
          <li style={S.bulletItem}>Full participation in all portfolio gains</li>
          <li style={S.bulletItem}>Partial withdrawals supported</li>
          <li style={S.bulletItem}>Best for long-term growth mindset investors</li>
        </ul>
      </div>

      {/* Tax Row */}
      <div style={{
        ...S.taxRow,
        flexDirection: isMobile ? "column" : "row",
        padding: isMobile ? "16px" : "20px 24px",
        gap: isMobile ? 10 : 16,
        marginBottom: isMobile ? 24 : 32,
      }}>
        <div style={{ ...S.taxIcon, alignSelf: isMobile ? "flex-start" : "center" }}>📄</div>
        <div style={S.taxText}>
          <h4 style={{ ...S.taxTitle, fontSize: isMobile ? 14 : 16 }}>Gain Tax at Exit — 24% on Profits Only</h4>
          <p style={S.taxDesc}>Applied only on realized gains. Zero profit = zero deduction. Your original investment always returned in full.</p>
        </div>
      </div>

      {/* Referral Matrix */}
      <div style={{ ...S.refBox, padding: isMobile ? "16px" : 24 }}>
        <div style={{ ...S.refBoxTitle, fontSize: isMobile ? 14 : 16 }}>📢 Where does the 24% go?</div>

        {[
          {
            label: "Phase 1 — Until Your Referrals Reach $10,000",
            rows: [
              { pct: "22%", desc: "SSN fund operations and community benefit" },
              { pct: "1%", desc: "Local Branch Development Fund" },
              { pct: "1%", desc: "Referral Reward — paid to the member who introduced you" },
            ],
          },
          {
            label: "Phase 2 — Once Individual Referrals Hit $10,000",
            rows: [
              { pct: "22%", desc: "SSN fund operations and community benefit" },
              { pct: "1%", desc: "Local Branch Development Fund" },
              { pct: "0.5%", desc: "Ongoing Referral Reward — continues to the referring member" },
              { pct: "0.5%", desc: "HQ New Branch Support Fund" },
            ],
          },
        ].map((phase) => (
          <div key={phase.label} style={S.refPhase}>
            <div style={{ ...S.refPhaseLabel, fontSize: isMobile ? 11 : 12 }}>{phase.label}</div>
            {phase.rows.map((row) => (
              <div key={row.pct + row.desc} style={{ ...S.refRow, padding: isMobile ? "8px 12px" : "10px 14px", gap: isMobile ? 12 : 16 }}>
                <div style={{ ...S.refPct, fontSize: isMobile ? 14 : 15, minWidth: isMobile ? 36 : 40 }}>{row.pct}</div>
                <div style={{ ...S.refDesc, fontSize: isMobile ? 12 : 13 }}>{row.desc}</div>
              </div>
            ))}
          </div>
        ))}

        {/* Payout Channels */}
        <div style={S.payoutOpts}>
          <h5 style={{ ...S.payoutTitle, fontSize: isMobile ? 13 : 14 }}>💰 How Referral Rewards Are Paid</h5>
          {[
            <><strong>SSN Units</strong> — reinvested at current unit price</>,
            <><strong>Physical Check</strong> — from your nearest SSN branch</>,
            <><strong>Direct Deposit</strong> — wired to your bank account</>,
          ].map((opt, i) => (
            <div key={i} style={{ ...S.payoutOpt, fontSize: isMobile ? 12 : 13 }}>
              <span style={S.arrowIcon}>→</span> Receive via {opt}
            </div>
          ))}
        </div>

        <p style={{ ...S.refNote, fontSize: isMobile ? 11 : 12 }}>
          * Referral rewards apply only on profitable exits. Phase 2 unlocks when cumulative referred investment reaches $10,000.
        </p>
      </div>
    </PageLayout>
  );
}

const S: Record<string, React.CSSProperties> = {
  exitCard: {
    background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: 12, padding: 24, marginBottom: 24,
  },
  ybadge: { display: "inline-block", padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, marginBottom: 14 },
  darkGreenBadge: { background: "rgba(29,158,117,0.15)", color: "var(--teal-light)" },
  lightGreenBadge: { background: "rgba(29,158,117,0.08)", color: "var(--text-primary)", border: "1px solid rgba(29,158,117,0.2)" },
  cardH3: { fontSize: 18, fontWeight: 600, color: "var(--text-primary)", marginBottom: 12 },
  bulletList: { margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 10 },
  bulletItem: { fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5 },
  taxRow: {
    display: "flex", gap: 16, padding: "20px 24px", background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, marginBottom: 32, alignItems: "center",
  },
  taxIcon: { fontSize: 24, flexShrink: 0 },
  taxText: { flex: 1 },
  taxTitle: { fontSize: 16, fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 },
  taxDesc: { fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5, margin: 0 },
  refBox: {
    background: "rgba(255,255,255,0.01)", border: "1px solid rgba(255,255,255,0.05)",
    borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", gap: 24,
  },
  refBoxTitle: { fontSize: 16, fontWeight: 600, color: "var(--text-primary)" },
  refPhase: { display: "flex", flexDirection: "column", gap: 10 },
  refPhaseLabel: { fontSize: 12, color: "var(--teal-light)", fontWeight: 600, letterSpacing: "0.02em", marginBottom: 2 },
  refRow: { display: "flex", alignItems: "center", gap: 16, background: "rgba(255,255,255,0.02)", padding: "10px 14px", borderRadius: 8 },
  refPct: { fontSize: 15, fontWeight: 700, color: "var(--text-primary)", minWidth: 40, flexShrink: 0 },
  refDesc: { fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.4 },
  payoutOpts: { marginTop: 8, display: "flex", flexDirection: "column", gap: 10 },
  payoutTitle: { fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 },
  payoutOpt: { fontSize: 13, color: "var(--text-secondary)", display: "flex", alignItems: "flex-start", gap: 8, lineHeight: 1.5 },
  arrowIcon: { color: "var(--teal-light)", fontWeight: "bold", flexShrink: 0 },
  refNote: { fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", marginTop: 4 },
};
